import React, { useEffect, useState } from 'react';
import api from '../utils/api';

export default function SuperAdminDashboard() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/org/organizations')
      .then(({ data }) => setOrgs(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const totalUsers = orgs.reduce((s, o) => s + (o.userCount || 0), 0);
  const activeOrgs = orgs.filter(o => o.isActive).length;
  const totalBotLicenses = orgs.reduce((s, o) => s + (o.botLicenseCount ?? o.licenseCount ?? 0), 0);
  const totalBcLicenses = orgs.reduce((s, o) => s + (o.broadcasterLicenseCount ?? 0), 0);

  const statCards = [
    { label: 'Total Organizations', value: orgs.length, color: '#6366f1', icon: '🏢' },
    { label: 'Active Orgs', value: activeOrgs, color: '#22c55e', icon: '✅' },
    { label: 'Total Users', value: totalUsers, color: '#f97316', icon: '👥' },
    { label: 'Bot Licenses', value: totalBotLicenses, color: '#60a5fa', icon: '🤖' },
    { label: 'Broadcaster Licenses', value: totalBcLicenses, color: '#c084fc', icon: '📢' },
  ];

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>System Overview</h1>
        <p style={s.sub}>You are the Super Admin — provider of this platform.</p>
      </div>

      <div style={s.statsGrid}>
        {statCards.map(c => (
          <div key={c.label} style={{ ...s.statCard, borderTop: `3px solid ${c.color}` }}>
            <span style={{ fontSize: 28 }}>{c.icon}</span>
            <span style={s.statVal}>{loading ? '—' : c.value}</span>
            <span style={s.statLabel}>{c.label}</span>
          </div>
        ))}
      </div>

      <div style={s.card}>
        <h2 style={s.cardTitle}>Organizations</h2>
        {loading ? (
          <p style={{ color: '#64748b' }}>Loading...</p>
        ) : orgs.length === 0 ? (
          <p style={{ color: '#64748b' }}>No organizations yet. Go to Organizations to create one.</p>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                {['Org Name', 'Apps', 'Bot Users/Lic', 'BC Users/Lic', 'Status'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orgs.map(org => {
                const botLic = org.botLicenseCount ?? org.licenseCount ?? 0;
                const bcLic = org.broadcasterLicenseCount ?? 0;
                const botUsers = org.botUserCount ?? org.userCount ?? 0;
                const bcUsers = org.broadcasterUserCount ?? 0;
                return (
                  <tr key={org._id}>
                    <td style={s.td}><span style={{ fontWeight: 600, color: '#f1f5f9' }}>{org.name}</span></td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {(org.allowedApps || ['bot']).map(a => (
                          <span key={a} style={{ ...s.featurePill, background: a === 'broadcaster' ? '#2d1a4a' : '#1e3a5f', color: a === 'broadcaster' ? '#c084fc' : '#60a5fa' }}>
                            {a === 'bot' ? '🤖 Bot' : '📢 BC'}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td style={s.td}>
                      <span style={{ color: botUsers >= botLic ? '#ef4444' : '#22c55e', fontWeight: 600 }}>{botUsers}</span>
                      <span style={{ color: '#64748b' }}> / {botLic}</span>
                    </td>
                    <td style={s.td}>
                      {(org.allowedApps || []).includes('broadcaster') ? (
                        <>
                          <span style={{ color: bcUsers >= bcLic ? '#ef4444' : '#22c55e', fontWeight: 600 }}>{bcUsers}</span>
                          <span style={{ color: '#64748b' }}> / {bcLic}</span>
                        </>
                      ) : <span style={{ color: '#334155' }}>—</span>}
                    </td>
                    <td style={s.td}>
                      <span style={{ ...s.badge, background: org.isActive ? '#166534' : '#7f1d1d', color: org.isActive ? '#86efac' : '#fca5a5' }}>
                        {org.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const FEATURE_LABELS = { dashboard: 'Dashboard', whatsapp: 'WhatsApp', chats: 'Chats', leads: 'Leads', programs: 'Programs' };

const s = {
  page: { padding: '32px 40px', flex: 1, background: '#0f172a', color: '#f1f5f9', overflowY: 'auto' },
  header: { marginBottom: 32 },
  title: { fontSize: 30, fontWeight: 700, margin: 0, color: '#fff' },
  sub: { color: '#64748b', marginTop: 6, fontSize: 14 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 32 },
  statCard: { background: '#1e293b', borderRadius: 12, padding: '24px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, border: '1px solid #334155' },
  statVal: { fontSize: 36, fontWeight: 700, color: '#f1f5f9' },
  statLabel: { fontSize: 13, color: '#64748b', textAlign: 'center' },
  card: { background: '#1e293b', borderRadius: 12, padding: 24, border: '1px solid #334155' },
  cardTitle: { fontSize: 18, fontWeight: 600, marginBottom: 20, color: '#e2e8f0' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', color: '#64748b', fontSize: 13, borderBottom: '1px solid #334155' },
  td: { padding: '14px 12px', borderBottom: '1px solid #0f172a', fontSize: 14, color: '#cbd5e1' },
  badge: { padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600 },
  featurePill: { background: '#1e3a5f', color: '#60a5fa', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 },
};
